import { useEffect, useRef } from "react";

import { debounce, throttle } from "es-toolkit";
import { observer } from "mobx-react-lite";

import { AwarenessPointer } from "@/components/awareness/AwarenessPointer";
import {
  AWARENESS_POINTER_HIDE_TIMEOUT,
  AWARENESS_POINTER_MOVE_THROTTLE_INTERVAL,
  AWARENESS_SCROLL_DEBOUNCE_INTERVAL,
} from "@/components/awareness/constants";

import type { DocumentModel } from "@/models/DocumentModel";
import type { EditorView } from "@tiptap/pm/view";

type Props = {
  doc: DocumentModel;
};

function getPointerPosition(clientX: number, clientY: number, view: EditorView | undefined) {
  const left = clientX + window.scrollX;
  const top = clientY + window.scrollY;

  let rel: { pos: number; dx: number; dy: number } | null = null;

  if (view) {
    const pos = view.posAtCoords({ left: clientX, top: clientY })?.pos ?? null;
    if (pos !== null) {
      const coords = view.coordsAtPos(pos);
      rel = {
        pos,
        dx: clientX - coords.left,
        dy: clientY - coords.top,
      };
    }
  }

  return { left, top, rel };
}

export const AwarenessPointers = observer(function AwarenessPointers({ doc }: Props) {
  const { awareness } = doc;
  const { tiptap } = doc.editor;

  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!awareness) {
      return () => {};
    }

    let rafHandle: number | null = null;

    let lastClientX: number | null = null;
    let lastClientY: number | null = null;

    const handleMouseMove = throttle((event: MouseEvent) => {
      const { clientX, clientY } = event;

      lastClientX = clientX;
      lastClientY = clientY;

      rafHandle = requestAnimationFrame(() => {
        awareness?.setPointer(getPointerPosition(clientX, clientY, tiptap?.view));

        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }

        hideTimeoutRef.current = setTimeout(() => {
          awareness?.setPointer(null);
        }, AWARENESS_POINTER_HIDE_TIMEOUT);
      });
    }, AWARENESS_POINTER_MOVE_THROTTLE_INTERVAL);

    const handleAwarenessScroll = debounce(() => {
      const clientX = lastClientX;
      const clientY = lastClientY;

      if (clientX === null || clientY === null) {
        return;
      }

      rafHandle = requestAnimationFrame(() => {
        awareness?.setPointer(getPointerPosition(clientX, clientY, tiptap?.view));

        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }

        hideTimeoutRef.current = setTimeout(() => {
          awareness?.setPointer(null);
        }, AWARENESS_POINTER_HIDE_TIMEOUT);
      });
    }, AWARENESS_SCROLL_DEBOUNCE_INTERVAL);

    document.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("scroll", handleAwarenessScroll);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("scroll", handleAwarenessScroll);

      handleAwarenessScroll.cancel();
      handleMouseMove.cancel();

      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }

      if (rafHandle) {
        cancelAnimationFrame(rafHandle);
      }
    };
  }, [awareness, tiptap]);

  if (!awareness?.clientIds.length || !tiptap) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[200] overflow-hidden pointer-events-none">
      {awareness
        ? awareness.clientIds.map((clientId) => (
            <AwarenessPointer
              key={clientId}
              awareness={awareness}
              clientId={clientId}
              tiptap={tiptap}
            />
          ))
        : null}
    </div>
  );
});
